import React from 'react';

import castArray from 'lodash/castArray';
import isEmpty from 'lodash/isEmpty';

import { useQuery } from '@lumapps/base-api/react-query';
import { list, communitiesQueryKeys } from '@lumapps/communities/api';
import { useCommunities } from '@lumapps/communities/hooks/useCommunities';
import { CommunitiesListRequestParams, Community, RenderingType } from '@lumapps/communities/types';
import { instanceIdSelector } from '@lumapps/instance/ducks/selectors';
import { useSelector } from '@lumapps/redux/react';
import { currentLanguageSelector } from '@lumapps/translations/ducks/selectors';
import { BaseLoadingStatus } from '@lumapps/utils/types/BaseLoadingStatus';

export interface UseSpaceSelectOptions {
    /** Currently selected space id(s), used to resolve the matching space objects. */
    value?: string | string[];
    /**
     * Extra params forwarded to the underlying communities `list` call
     * (e.g. `instanceId`, `privacy` for visibility filtering, `sortOrder`).
     */
    params?: Partial<CommunitiesListRequestParams>;
    /** Whether the searchable list should be fetched on mount (defaults to lazy on open). */
    fetchOnMount?: boolean;
}

export interface UseSpaceSelect {
    /** Searchable, paginated list of spaces to display as options. */
    spaces: Community[];
    /** Space objects matching the provided `value` id(s). */
    selectedSpaces: Community[];
    /** Loading status of the searchable list. */
    status: BaseLoadingStatus;
    /** Whether the selected space id(s) are being resolved. */
    isResolvingValue: boolean;
    /** Search the list of spaces by text. */
    onSearch: (searchText: string) => void;
    /** Load the next page of spaces. */
    fetchMore: () => void;
    /** Trigger the initial fetch of the list. */
    fetch: () => void;
    /** Reset the list state. */
    reset: () => void;
}

/**
 * Hook to retrieve, search and paginate spaces for a select component, and to resolve
 * selected space id(s) into their matching space objects.
 *
 * Spaces are communities with the `space` rendering type, so this builds on top of
 * `useCommunities` and the communities `list` API.
 */
export const useSpaceSelect = ({ value, params, fetchOnMount }: UseSpaceSelectOptions = {}): UseSpaceSelect => {
    const lang = useSelector(currentLanguageSelector);
    const instanceId = useSelector(instanceIdSelector);

    const listParams = React.useMemo<Partial<CommunitiesListRequestParams>>(
        () => ({
            instanceId: [instanceId],
            ...params,
            lang,
            renderingTypes: [RenderingType.space],
        }),
        [instanceId, lang, params],
    );

    const { communities, status, onSearch, fetchMore, fetch, reset } = useCommunities({
        params: listParams,
        fetchOnMount,
    });

    // Normalize the value to a list of ids.
    const selectedIds = React.useMemo(() => (value !== undefined ? castArray(value) : []), [value]);

    // Resolve the selected ids that are not already present in the loaded list.
    const missingIds = React.useMemo(
        () => selectedIds.filter((id) => !communities.some((space) => space.id === id)),
        [selectedIds, communities],
    );

    // Resolve selected ids using only the keys needed to fetch by id. Caller list-filters
    // (e.g. `privacy`, `sortOrder`) are intentionally left out so a selected space always
    // resolves for display, even if it wouldn't match the filtered list.
    const resolveParams = React.useMemo<Partial<CommunitiesListRequestParams>>(
        () => ({
            instanceId: listParams.instanceId,
            lang: listParams.lang,
            renderingTypes: listParams.renderingTypes,
            ids: missingIds,
            maxResults: missingIds.length,
        }),
        [listParams.instanceId, listParams.lang, listParams.renderingTypes, missingIds],
    );

    const { data: resolvedData, isInitialLoading: isResolvingValue } = useQuery({
        enabled: !isEmpty(missingIds),
        queryKey: communitiesQueryKeys.list(resolveParams),
        queryFn: async () => {
            const { data } = await list(resolveParams);
            return data;
        },
    });

    // Merge resolved spaces with the ones already loaded in the list, preserving the value order.
    const selectedSpaces = React.useMemo(() => {
        if (isEmpty(selectedIds)) {
            return [];
        }

        const pool = [...communities, ...(resolvedData?.items ?? [])];

        return selectedIds
            .map((id) => pool.find((space) => space.id === id))
            .filter((space): space is Community => space !== undefined);
    }, [selectedIds, communities, resolvedData]);

    return {
        spaces: communities,
        selectedSpaces,
        status,
        isResolvingValue,
        onSearch,
        fetchMore,
        fetch,
        reset,
    };
};
